import React, { useEffect, useMemo, useState } from "react";
import { Check, Pencil, Plus, Trash2 } from "lucide-react";
import api, { fmtErr } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const empty = { title: "", amount: "", due_date: "", cari_id: "none", bank_account_id: "none", notes: "" };

export default function Payments() {
  const { t, fmt, fmtDate } = useI18n();
  const { canEdit } = useAuth();
  const [rows, setRows] = useState([]);
  const [banks, setBanks] = useState([]);
  const [caris, setCaris] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [error, setError] = useState("");

  const load = async () => {
    const [p, b, c] = await Promise.all([api.get("/payments"), api.get("/bank-accounts"), api.get("/cari")]);
    setRows(p.data);
    setBanks(b.data);
    setCaris(c.data);
  };
  useEffect(() => { load().catch(() => {}); }, []);

  const filtered = useMemo(() => rows.filter((r) => filter === "all" || r.status === filter), [rows, filter]);
  const today = new Date().toISOString().slice(0, 10);

  const openNew = () => { setEditing(null); setForm(empty); setError(""); setOpen(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({ ...r, cari_id: r.cari_id || "none", bank_account_id: r.bank_account_id || "none" });
    setError("");
    setOpen(true);
  };

  const save = async () => {
    const payload = {
      ...form,
      amount: parseFloat(form.amount),
      cari_id: form.cari_id === "none" ? null : form.cari_id,
      bank_account_id: form.bank_account_id === "none" ? null : form.bank_account_id,
    };
    try {
      if (editing) await api.put(`/payments/${editing.id}`, payload);
      else await api.post("/payments", payload);
      setOpen(false);
      load().catch(() => {});
    } catch (e) {
      setError(fmtErr(e));
    }
  };

  const markPaid = async (id) => {
    await api.post(`/payments/${id}/mark-paid`);
    toast.success(t.payP.markPaid);
    load().catch(() => {});
  };

  const remove = async (id) => {
    if (!window.confirm(t.app.confirmDelete)) return;
    await api.delete(`/payments/${id}`);
    load().catch(() => {});
  };

  const filters = [
    { k: "pending", label: t.app.pending },
    { k: "paid", label: t.app.paid },
    { k: "all", label: t.app.all },
  ];

  return (
    <div className="space-y-5" data-testid="payments-page">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center rounded-full border border-slate-200 bg-white p-1" data-testid="payments-filter">
          {filters.map((f) => (
            <button key={f.k} onClick={() => setFilter(f.k)} data-testid={`payments-filter-${f.k}`}
              className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                filter === f.k ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900"
              }`}>
              {f.label}
            </button>
          ))}
        </div>
        {canEdit && (
          <Button onClick={openNew} data-testid="payment-new-button" className="ml-auto">
            <Plus className="mr-2 h-4 w-4" />{t.payP.new}
          </Button>
        )}
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200/70 bg-white shadow-sm" data-testid="payments-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-100 text-left font-mono text-[10px] uppercase tracking-widest text-slate-400">
              <th className="px-5 py-3.5 font-medium">{t.app.title}</th>
              <th className="hidden px-5 py-3.5 font-medium md:table-cell">{t.app.cari}</th>
              <th className="px-5 py-3.5 font-medium">{t.app.dueDate}</th>
              <th className="hidden px-5 py-3.5 font-medium lg:table-cell">{t.app.bank}</th>
              <th className="px-5 py-3.5 text-right font-medium">{t.app.amount}</th>
              <th className="px-5 py-3.5 font-medium">{t.app.status}</th>
              <th className="px-5 py-3.5 text-right font-medium">{t.app.actions}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="px-5 py-14 text-center text-sm text-slate-400">{t.app.noData}</td></tr>
            )}
            {filtered.map((r) => {
              const late = r.status === "pending" && r.due_date < today;
              return (
                <tr key={r.id} className="transition-colors hover:bg-slate-50" data-testid={`payment-row-${r.id}`}>
                  <td className="px-5 py-3.5 font-medium text-slate-900">{r.title}</td>
                  <td className="hidden px-5 py-3.5 text-slate-500 md:table-cell">{r.cari_name || "—"}</td>
                  <td className="whitespace-nowrap px-5 py-3.5">
                    <span className={`font-mono text-xs ${late ? "text-red-600" : "text-slate-500"}`}>{fmtDate(r.due_date)}</span>
                    {late && <span className="ml-2 rounded-full bg-red-50 px-2 py-0.5 font-mono text-[9px] uppercase text-red-600">{t.app.overdue}</span>}
                  </td>
                  <td className="hidden px-5 py-3.5 text-slate-500 lg:table-cell">{r.bank_account_name || "—"}</td>
                  <td className="tnum whitespace-nowrap px-5 py-3.5 text-right font-mono font-medium text-slate-900">{fmt(r.amount)}</td>
                  <td className="px-5 py-3.5">
                    <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${
                      r.status === "paid" ? "bg-emerald-50 text-emerald-600" : late ? "bg-red-50 text-red-500" : "bg-slate-100 text-slate-600"
                    }`}>
                      {r.status === "paid" ? `${t.payP.paidOn}${r.paid_at ? " · " + fmtDate(r.paid_at) : ""}` : t.app.pending}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-5 py-3.5 text-right">
                    {canEdit && r.status === "pending" && (
                      <>
                        <button onClick={() => markPaid(r.id)} aria-label={t.payP.markPaid} data-testid={`payment-mark-paid-${r.id}`}
                          title={t.payP.markPaid}
                          className="rounded-md p-1.5 text-slate-400 transition-colors hover:bg-emerald-50 hover:text-emerald-600">
                          <Check className="h-4 w-4" />
                        </button>
                        <button onClick={() => openEdit(r)} aria-label={t.app.edit} data-testid={`payment-edit-${r.id}`}
                          className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-900">
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button onClick={() => remove(r.id)} aria-label={t.app.delete} data-testid={`payment-delete-${r.id}`}
                          className="rounded-md p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="payment-dialog">
          <DialogHeader><DialogTitle>{editing ? t.payP.edit : t.payP.new}</DialogTitle></DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t.app.title}</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="payment-title-input" />
              </div>
              <div className="space-y-2">
                <Label>{t.app.amount}</Label>
                <Input type="number" step="0.01" min="0" value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="payment-amount-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t.app.dueDate}</Label>
                <Input type="date" value={form.due_date || ""} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="payment-date-input" />
              </div>
              <div className="space-y-2">
                <Label>{t.app.cari}</Label>
                <Select value={form.cari_id} onValueChange={(v) => setForm({ ...form, cari_id: v })} data-testid="payment-cari-select">
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">—</SelectItem>
                    {caris.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t.app.bank}</Label>
              <Select value={form.bank_account_id} onValueChange={(v) => setForm({ ...form, bank_account_id: v })} data-testid="payment-bank-select">
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  {banks.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t.app.notes}</Label>
              <Input value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="payment-notes-input" />
            </div>
            {error && <p className="rounded-md border border-red-100 bg-red-50 px-3.5 py-2.5 text-sm text-red-600" data-testid="payment-error">{error}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="payment-cancel-button">{t.app.cancel}</Button>
            <Button onClick={save} disabled={!form.title || !form.amount || !form.due_date} data-testid="payment-save-button">{t.app.save}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
